import React from "react";
import { Row, Table } from "antd";

import { constructDisplayPrice } from "utils/general";

import Title from "components/Title/Title";
import SectionContainer from "components/SectionContainer/SectionContainer";
import SummaryInfoCard from "components/SummaryInfoCard/SummaryInfoCard";

import {
  DescriptionForLabel,
  ExtraInfoText,
  FinancialValueText,
  Label,
} from "./Summary.styles";
import Locale from "react-intl-universal";

const constructFinancialDataSource = (financialInfo = {}) => [
  {
    label: Locale.get("summary.summaryAdviseeDetail.tMonthlySalary"),
    value: constructDisplayPrice(financialInfo.monthlyIncomeAmt || 0, false),
  },
  {
    label: Locale.get("summary.tMonthlyExpense"),
    desc: Locale.get("summary.summaryAdviseeDetail.monthlyExpenseDesc"),
    value: constructDisplayPrice(
      financialInfo.totalMonthlyExpensesAmt || 0,
      false
    ),
  },
  {
    label: Locale.get("summary.tLoanAmount"),
    value: constructDisplayPrice(financialInfo.totalLoanPaymentAmt || 0, false),
  },
  {
    label: Locale.get("summary.ideLabel"),
    value: constructDisplayPrice(
      financialInfo.immediateDeathExpensesAmt || 0,
      false
    ),
    remarks: Locale.get("summary.extraIde"),
  },
  {
    label: Locale.get("summary.minYearResponsible"),
    value: financialInfo.yearsOfDependency || 0,
    remarks: Locale.get("summary.extraYear"),
  },
];

const financialTableColumns = [
  {
    dataIndex: "label",
    key: "label",
    render: (value, record) => (
      <div>
        <Label>{value}</Label>
        <DescriptionForLabel>{record.desc}</DescriptionForLabel>
      </div>
    ),
  },
  {
    dataIndex: "value",
    key: "value",
    render: (value) => <FinancialValueText>{value}</FinancialValueText>,
  },
  {
    dataIndex: "remarks",
    key: "remarks",
    render: (value) => <ExtraInfoText>{value}</ExtraInfoText>,
  },
];

const getInsuranceTableColumns = (title) => [
  {
    title,
    dataIndex: "label",
    key: "label",
    render: (value, record) => (
      <div>
        <Label>{value}</Label>
        <DescriptionForLabel>{record.desc}</DescriptionForLabel>
      </div>
    ),
  },
  {
    dataIndex: "value",
    key: "value",
    render: (value) => <FinancialValueText>{value}</FinancialValueText>,
  },
];

const constructBasicInfoDataSource = (insurancesInfo) => [
  {
    label: Locale.get("summary.summaryAdviseeDetail.monthlyPremium"),
    value: constructDisplayPrice(insurancesInfo.monthlyPremium || 0, false),
  },
];

const constructPlanCoverageDataSource = (insurancesInfo) => [
  {
    label: Locale.get("summary.summaryAdviseeDetail.deathBenefit"),
    value: constructDisplayPrice(insurancesInfo.deathBenefit || 0, false),
  },
  {
    label: Locale.get("summary.summaryAdviseeDetail.tpdb"),
    value: constructDisplayPrice(insurancesInfo.tpdBenefit || 0, false),
  },
  {
    label: Locale.get("summary.summaryAdviseeDetail.oadb"),
    desc: Locale.get("summary.summaryAdviseeDetail.oadbDesc"),
    value: constructDisplayPrice(
      insurancesInfo.oldAgeDisabilityBenefit || 0,
      false
    ),
  },
  {
    label: Locale.get("summary.summaryAdviseeDetail.pab"),
    value: constructDisplayPrice(
      insurancesInfo.personalAccidentBenefit || 0,
      false
    ),
  },
  {
    label: Locale.get("summary.summaryAdviseeDetail.totalCib"),
    value: constructDisplayPrice(
      insurancesInfo.totalEarlyCIBenefit || 0,
      false
    ),
  },
  {
    label: Locale.get("summary.summaryAdviseeDetail.cib"),
    value: constructDisplayPrice(insurancesInfo.ciBenefit || 0, false),
  },
  {
    label: Locale.get("summary.summaryAdviseeDetail.disabilityIncome"),
    value: constructDisplayPrice(
      insurancesInfo.permanentDisabilityMonthlyIncome || 0,
      false
    ),
  },
];

const constructHospitalizationDataSource = (insurancesInfo) => [
  {
    label: Locale.get("summary.summaryAdviseeDetail.dailyBenefit"),
    value: constructDisplayPrice(insurancesInfo.dailyBenefit || 0, false),
  },
];

const constructMedicalDataSource = (insurancesInfo) => {
  const isYearlyLimitUnlimited =
    insurancesInfo.medicalCardYearlyLimitAmt !== undefined &&
    insurancesInfo.medicalCardYearlyLimitAmt < 0;
  const isLifetimeLimitUnlimited =
    insurancesInfo.medicalCardLifetimeLimitAmt !== undefined &&
    insurancesInfo.medicalCardLifetimeLimitAmt < 0;
  return [
    {
      label: Locale.get("summary.summaryAdviseeDetail.rab"),
      value: constructDisplayPrice(
        insurancesInfo.medicalCardRoomAndBoardAmt || 0,
        false
      ),
    },
    {
      label: Locale.get("summary.summaryAdviseeDetail.yearlyLimit"),
      value: isYearlyLimitUnlimited
        ? "Unlimited"
        : constructDisplayPrice(
            insurancesInfo.medicalCardYearlyLimitAmt || 0,
            false
          ),
    },
    {
      label: Locale.get("summary.summaryAdviseeDetail.lifetimeLimit"),
      value: isLifetimeLimitUnlimited
        ? "Unlimited"
        : constructDisplayPrice(
            insurancesInfo.medicalCardLifetimeLimitAmt || 0,
            false
          ),
    },
  ];
};

const Summary = ({
  financialInfo = {},
  insurancesInfo = {},
  personalInfo = {},
}) => {
  return (
    <>
      <Title marginBottom="24px">
        {Locale.get("summary.summaryAdviseeDetail.pageTitle")}
      </Title>
      <SummaryInfoCard
        name={personalInfo.name}
        age={personalInfo.age}
        dob={personalInfo.dob}
        occupation={personalInfo.occupation}
        dependentYears={personalInfo.minYearsOfDependency}
        noOfDependentFamilyMember={personalInfo.noOfDependentFamilyMember}
      />
      <SectionContainer>
        <Title marginBottom="8px">
          {Locale.get("summary.summaryAdviseeDetail.sectionContainerTitle_0")}
        </Title>
        <Row>
          <Table
            dataSource={constructFinancialDataSource(financialInfo)}
            columns={financialTableColumns}
            pagination={false}
            rowKey={(record) => record.label}
            showHeader={false}
            style={{ width: "100%" }}
          />
        </Row>
      </SectionContainer>
      <Title marginBottom="16px">
        {Locale.get("summary.summaryAdviseeDetail.sectionContainerTitle_1")}
      </Title>
      <Row>
        <Table
          dataSource={constructBasicInfoDataSource(insurancesInfo)}
          columns={getInsuranceTableColumns()}
          pagination={false}
          rowKey={(record) => record.label}
          showHeader={false}
          style={{ width: "100%" }}
        />
      </Row>
      <Row>
        <Table
          dataSource={constructPlanCoverageDataSource(insurancesInfo)}
          columns={getInsuranceTableColumns(
            Locale.get("summary.summaryAdviseeDetail.planCoverage")
          )}
          pagination={false}
          rowKey={(record) => record.label}
          style={{ width: "100%" }}
        />
      </Row>
      <Row>
        <Table
          dataSource={constructHospitalizationDataSource(insurancesInfo)}
          columns={getInsuranceTableColumns(
            Locale.get("summary.summaryAdviseeDetail.hospitalizationBenefit")
          )}
          pagination={false}
          rowKey={(record) => record.label}
          style={{ width: "100%" }}
        />
      </Row>
      <Row>
        <Table
          dataSource={constructMedicalDataSource(insurancesInfo)}
          columns={getInsuranceTableColumns(
            Locale.get("summary.summaryAdviseeDetail.medCard")
          )}
          pagination={false}
          rowKey={(record) => record.label}
          style={{ width: "100%" }}
        />
      </Row>
    </>
  );
};

export default Summary;
